// Quantum Portfolio Optimizer — QUBO solved via simulated annealing.
// Braket/D-Wave ready: swap solver in worker when AWS keys are set.

import type { Tab } from './index';
import { esc } from '../utils';
import { workerFetch } from '../worker';
import { store } from '../state';
import type { Ticker } from '../state';
import { showToast } from '../toast';

interface Allocation {
  ticker:         string;
  optimalWeight:  number;
  expectedReturn: number;
}
interface OptResult {
  allocations:     Allocation[];
  portfolioReturn: number;
  portfolioRisk:   number;
  sharpe:          number;
  solver:          string;
  solvedAt:        string;
  error?:          string;
}

export class QuantumOptimizerTab implements Tab {
  readonly id = 'quantum-optimizer';
  private result: OptResult | null = null;
  private running = false;
  private usingFallback = false;

  private el(id: string) { return document.getElementById(id) as HTMLElement; }

  init(): void {
    document.getElementById('qo-run-btn')?.addEventListener('click', () => this.run());
    document.getElementById('qo-max-pos')?.addEventListener('input', (e) => {
      const v = (e.target as HTMLInputElement).value;
      const el = document.getElementById('qo-max-pos-val');
      if (el) el.textContent = v + '%';
    });
    document.getElementById('qo-risk')?.addEventListener('input', (e) => {
      const v = parseFloat((e.target as HTMLInputElement).value);
      const el = document.getElementById('qo-risk-val');
      if (el) el.textContent = ['Conservative', 'Balanced', 'Aggressive', 'Max Return'][Math.round(v) - 1] ?? 'Balanced';
    });
  }

  render(): void {
    if (this.result) this.renderResult(this.result);
  }

  private async run(): Promise<void> {
    if (this.running) return;

    const tickers = (store.get('tickers') as Ticker[])
      .map(h => (h.Ticker ?? '').toUpperCase().trim()).filter(Boolean);

    if (tickers.length < 2) {
      showToast('Need at least 2 holdings — sync your portfolio first', 'warn');
      return;
    }

    const alpacaKey    = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const alpacaSecret = localStorage.getItem('meridian_alpaca_secret') ?? '';
    const maxPosition  = parseFloat((document.getElementById('qo-max-pos') as HTMLInputElement)?.value ?? '20') / 100;
    const riskAversion = parseFloat((document.getElementById('qo-risk')    as HTMLInputElement)?.value ?? '2');

    this.running = true;
    this.usingFallback = false;
    const btn = document.getElementById('qo-run-btn') as HTMLButtonElement;
    if (btn) { btn.disabled = true; btn.textContent = '⟳ SOLVING…'; }

    const body = this.el('qo-body');
    body.innerHTML = `
      <div style="padding:32px;text-align:center;">
        <div style="font-family:var(--mono);font-size:12px;color:var(--text3);margin-bottom:12px;">
          Fetching price data · building QUBO · running simulated annealing…
        </div>
        <div style="font-size:10px;color:var(--text3);">${tickers.length} assets · max position ${Math.round(maxPosition*100)}%</div>
      </div>`;

    try {
      // Step 1: get probability scores to use as expected returns
      let expectedReturns: number[];
      try {
        const probData = await workerFetch<{ results: Array<{ ticker: string; probability: number }> }>('/api/probability', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tickers, alpacaKey, alpacaSecret }),
          signal: AbortSignal.timeout(120000),
        });
        const probMap: Record<string, number> = {};
        (probData.results ?? []).forEach(r => { probMap[r.ticker] = r.probability; });
        expectedReturns = tickers.map(t => probMap[t] ?? 0.5);
      } catch {
        expectedReturns = tickers.map(() => 0.5);
        showToast('Probability engine unavailable — using equal priors (50%)', 'warn');
        this.usingFallback = true;
      }

      // Step 2: run QUBO optimizer
      const result = await workerFetch<OptResult>('/api/quantum-optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers, expectedReturns, maxPosition, riskAversion }),
        signal: AbortSignal.timeout(60000),
      });

      this.result = result;
      this.renderResult(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      body.innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    } finally {
      this.running = false;
      if (btn) { btn.disabled = false; btn.textContent = '▶ OPTIMIZE'; }
    }
  }

  private renderResult(r: OptResult): void {
    if (r.error) {
      this.el('qo-body').innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(r.error)}</div>`;
      return;
    }

    const totalTickers = (store.get('tickers') as Ticker[]).length;
    const excluded = totalTickers - r.allocations.filter(a => a.optimalWeight > 0).length;
    const equalWeight = Math.round(100 / totalTickers);

    const rows = r.allocations.map(a => {
      const pct     = Math.round(a.optimalWeight * 100);
      const retCol  = a.expectedReturn >= 62 ? 'var(--green)' : a.expectedReturn <= 45 ? 'var(--red)' : 'var(--warn)';
      const diff    = pct - equalWeight;
      const diffStr = diff > 0 ? `+${diff}%` : diff < 0 ? `${diff}%` : '—';
      const diffCol = diff > 0 ? 'var(--green)' : diff < 0 ? 'var(--red)' : 'var(--text3)';
      const barW    = Math.min(100, pct * 4);

      return `
        <tr style="border-bottom:1px solid var(--border);">
          <td style="padding:10px 14px;font-family:var(--mono);font-size:13px;font-weight:700;color:var(--accent);">${esc(a.ticker)}</td>
          <td style="padding:10px 14px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <div style="width:80px;height:5px;background:var(--bg3);border-radius:2px;overflow:hidden;">
                <div style="width:${barW}%;height:100%;background:var(--accent);border-radius:2px;"></div>
              </div>
              <span style="font-family:var(--mono);font-size:13px;font-weight:700;color:${pct>0?'var(--text)':'var(--text3)'};">${pct}%</span>
            </div>
          </td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:${diffCol};">${diffStr}</td>
          <td style="padding:10px 14px;font-family:var(--mono);font-size:12px;color:${retCol};">${a.expectedReturn}%</td>
        </tr>`;
    }).join('');

    const sharpeCol = r.sharpe >= 1 ? 'var(--green)' : r.sharpe >= 0.5 ? 'var(--warn)' : 'var(--red)';

    this.el('qo-body').innerHTML = `
      <div style="display:flex;gap:12px;flex-wrap:wrap;margin-bottom:20px;">
        ${this.statCard('Expected Return', r.portfolioReturn + '%', r.portfolioReturn >= 55 ? 'var(--green)' : 'var(--warn)')}
        ${this.statCard('Portfolio Risk (vol)', r.portfolioRisk + '%', 'var(--text2)')}
        ${this.statCard('Sharpe Ratio', r.sharpe.toFixed(2), sharpeCol)}
        ${this.statCard('Positions', r.allocations.filter(a=>a.optimalWeight>0).length + ' / ' + r.allocations.length, 'var(--text2)')}
      </div>

      ${this.usingFallback ? `<div style="background:rgba(255,193,7,0.08);border:1px solid rgba(255,193,7,0.3);border-radius:4px;padding:10px 14px;font-size:11px;color:var(--warn);margin-bottom:16px;font-family:var(--mono);">
        ⚠ Probability engine unavailable — using equal 50% priors for all holdings. Model Prob column reflects assumed baseline, not real signal. Re-run Probability Engine tab first for signal-weighted optimization.
      </div>` : ''}
      ${excluded > 0 ? `<div style="background:rgba(255,193,7,0.08);border:1px solid rgba(255,193,7,0.2);border-radius:4px;padding:10px 14px;font-size:11px;color:var(--warn);margin-bottom:16px;font-family:var(--mono);">
        ${excluded} position${excluded>1?'s':''} excluded (optimizer allocated 0% — consider trimming or reviewing thesis)
      </div>` : ''}

      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Ticker</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Optimal Weight</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;" title="vs equal-weight baseline">vs Equal</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Model Prob</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>

      <div style="margin-top:20px;padding:12px 16px;background:var(--panel);border:1px solid var(--border);border-radius:4px;font-size:10px;color:var(--text3);line-height:1.7;">
        <div style="font-weight:700;color:var(--text2);margin-bottom:4px;font-family:var(--mono);">⚛ ${esc(r.solver)}</div>
        Weights optimised as a QUBO (Quadratic Unconstrained Binary Optimization) — the same problem formulation
        solved by D-Wave quantum annealers. Currently running classical simulated annealing.
        Add <code style="background:var(--bg3);padding:1px 4px;border-radius:2px;">AWS_ACCESS_KEY_ID</code> +
        <code style="background:var(--bg3);padding:1px 4px;border-radius:2px;">BRAKET_DEVICE_ARN</code> as
        wrangler secrets to upgrade to real QPU with no other code changes.
      </div>`;
  }

  private statCard(label: string, value: string, color: string): string {
    return `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:14px 18px;min-width:130px;flex:1;">
        <div style="font-size:10px;color:var(--text3);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:6px;">${esc(label)}</div>
        <div style="font-family:var(--mono);font-size:20px;font-weight:700;color:${color};">${esc(value)}</div>
      </div>`;
  }
}
